import React from 'react';
import { Button, Progress } from 'reactstrap';
import { toast } from 'react-toastify';
import { isEqual } from 'lodash-es';
import {
    faSave,
    faFileInvoiceDollar,
    faCheckCircle,
    faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormPrompt } from '@s20/components/common/FormPrompt';
import { clone } from '@s20/common/util/object';
import { httpClient } from '@s20/services/httpClient';
import CommonContext, {
    ApiRoutes,
    AppNavPaths,
    ServiceResponseResult,
} from '../Common';
import {
    AppPageForm,
    FlexCenterRow,
    FormGroupColumn,
    GroupedRow,
    Input,
    onFieldChange,
    onReactSelectChanged,
    ToastMessage,
} from '../common/forms/FormElements';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import { handleFormSaveError } from '../common/forms/ValidationError';

class JotForm {
    id = null;
    description = '';
    formId = '';
    jotFormTypeId = null;
}

class JotFormForm extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);

        this.formRef = React.createRef();

        this.state = {
            originalData: new JotForm(),
            jotForm: new JotForm(),
            jotFormTypes: [],
            loading: true,
            saving: false,
        };

        this.handlers.change = this.handlers.change.bind(this);
        this.handlers.select = this.handlers.select.bind(this);
    }

    componentDidMount() {
        return this.populateState();
    }

    componentDidUpdate(prevProps) {
        if (
            prevProps &&
            this.props.match.params.id !== (prevProps.match.params ?? {}).id
        ) {
            this.populateState();
        }
    }

    onClearErrors = () => this.setState({ errors: {} });

    onSubmit = async () => {
        this.onClearErrors();
        this.setSaving(true);

        const cloned = clone(this.state.jotForm);


        if (this.props.location.pathname == AppNavPaths.JotFormNew) {
            await this.create(cloned);
        } else {
            await this.update(cloned);
        }
    };

    setSaving = (b) => this.setState({ saving: b });

    create = async (jotForm) => {
        jotForm.id = 0;

        const response = await httpClient
            .post(ApiRoutes.jotForms.create(), jotForm)
            .catch(this.handleSaveError);

        if (response) {
            if (response.result === ServiceResponseResult.Ok) {
                toast.success(
                    <ToastMessage
                        icon={faCheckCircle}
                        header="Save Successful"
                        message="Saved."
                    />,
                );

                this.context.history.push(
                    `${AppNavPaths.JotForm}/${response.data}`,
                );
            } else {
                toast.error(
                    <ToastMessage
                        icon={faExclamationTriangle}
                        header="There was a problem saving"
                        message={`${response.message}`}
                    />,
                );
            }
        }
        this.setSaving(false);
    };

    handleSaveError = (err) => handleFormSaveError(this, err);

    handlers = {
        change: onFieldChange,
        select: onReactSelectChanged,
    };

    async populateState() {
        const { id } = this.props.match.params;
        const [jotForm, jotFormTypes] = await Promise.all([
            id ? httpClient.js(ApiRoutes.jotForms.getById(id)) : new JotForm(),
            httpClient.js(ApiRoutes.typeAheads.jotFormTypes()),
        ]);

        this.setState({
            originalData: { ...jotForm },
            jotFormTypes: [...jotFormTypes],
            jotForm: {
                ...jotForm,
            },
            loading: false,
            saving: false,
        });
    }

    update = async (jotForm) => {
        const response = await httpClient
            .put(ApiRoutes.jotForms.update(jotForm.id), jotForm)
            .catch(this.handleSaveError);

        if (response) {
            if (response.result === ServiceResponseResult.Ok) {
                this.setState({
                    saving: false,
                    formValidated: false,
                });

                this.populateState();

                toast.success(
                    <ToastMessage
                        icon={faCheckCircle}
                        header="Save Successful"
                        message="Jot Form saved."
                    />,
                );
            } else {
                toast.error(
                    <ToastMessage
                        icon={faExclamationTriangle}
                        header="There was a problem saving"
                        message={`${response.message}`}
                    />,
                );
            }
        }
        this.setSaving(false);
    };

    renderForm() {
        const {
            jotFormTypes,
            jotForm,
            originalData,
            saving,
            errors,
            loading,
            formValidated,
        } = this.state;

        if (!jotForm) {
            return '';
        }

        const newJotForm = parseInt(jotForm.id ?? 0) <= 0;

        return (
            <>
                <FormPrompt
                    when={!saving && !isEqual(originalData, jotForm)}
                    message="You have unsaved changes, are you sure you want to leave?"
                />
                <AppPageForm
                    formShown={this.context.formIsOpen}
                    formId="jotFormForm"
                    formHeadingIcon={faFileInvoiceDollar}
                    formHeading={newJotForm ? 'New Jot Form' : 'Edit Jot Form'}
                    formName="jotFormForm"
                    formRef={this.formRef}
                    onSubmit={this.onSubmit}
                    setIsValidated={(value) => {
                        this.setState({ formValidated: value });
                    }}
                    isValidated={formValidated}
                    saving={saving}
                    errors={errors}
                    loading={loading}
                    backUrl={AppNavPaths.JotForms}
                    backLabel="Back to Jot Forms"
                >
                    <GroupedRow>
                        <FormGroupColumn>
                            <Input
                                label="Jot Form Name"
                                id="description"
                                name="jotForm.description"
                                value={jotForm.description ?? ''}
                                onChange={this.handlers.change}
                                placeholder="Enter Name"
                                error="Name is required"
                                required
                            />

                            <ValidatedSelect
                                id="jotFormTypeId"
                                name="jotForm.jotFormTypeId"
                                label="Jot Form Type"
                                required
                                options={jotFormTypes}
                                value={
                                    (jotFormTypes ?? []).find(
                                        (s) =>
                                            s.value ===
                                            jotForm.jotFormTypeId,
                                    ) ?? ''
                                }
                                onChange={this.handlers.select}
                                error="A Type selection is required."
                            />

                            <Input
                                label="Jot Form Id"
                                id="formId"
                                name="jotForm.formId"
                                value={jotForm.formId ?? ''}
                                onChange={this.handlers.change}
                                placeholder="Enter Jot Form Id"
                                error="Jot Form Id is required."
                                required
                            />
                        </FormGroupColumn>
                    </GroupedRow>

                    <FlexCenterRow className="mb-3">
                        <Button
                            size="sm"
                            type="submit"
                            color="primary"
                            name="countyGroupForm"
                        >
                            <FontAwesomeIcon
                                className="mr-1"
                                icon={faSave}
                            />
                            {newJotForm ? 'Save New Jot Form' : 'Save'}
                        </Button>
                    </FlexCenterRow>
                </AppPageForm>
            </>
        );
    }

    render() {
        const { loading } = this.state;

        if (loading) {
            return <Progress />;
        }

        return this.renderForm();
    }
}
export default JotFormForm;
